import type { ColumnDef } from '@tanstack/react-table';
import { ImageIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ItemResponseDTO } from '@/api/generated/model';
import { formatDateTime } from './lib/display';
import { itemTypeLabel, reportStatusDisplay } from './lib/listDisplay';
import { safeMediaSrc } from '@/lib/mediaUrl';
import { userReportTypeLabel } from '@/lib/enums';
import type { ProactiveCase, ModerationActionEntry } from './api';

/**
 * DataGrid columns for the report queue (doc 09 §4.1). Column ids `type` / `reportType` / `status`
 * double as the filter columnKeys wired to the server params in api.ts.
 *
 * Mapped to the REAL admin-api `ItemResponseDTO` fields: contentType, avatarImage, displayName,
 * thumbnail, content, userReportType, reportedBy, reportedAt, status.
 */
export const moderationColumns: ColumnDef<ItemResponseDTO, unknown>[] = [
  {
    id: 'type',
    header: 'Type',
    cell: ({ row }) => {
      const r = row.original;
      return (
        <div className="flex flex-col">
          <span className="text-sm font-medium">{itemTypeLabel(r.contentType)}</span>
          {/* fileType is not in the real DTO — no sub-label. */}
        </div>
      );
    },
  },
  {
    id: 'content',
    header: 'Content / User',
    cell: ({ row }) => {
      const r = row.original;
      const avatar = safeMediaSrc(r.avatarImage ?? r.thumbnail);
      const primary = r.displayName || r.content || '—';
      const handle = r.u_name;
      return (
        <div className="flex items-center gap-3">
          {avatar ? (
            <img
              src={avatar}
              alt=""
              className="size-10 shrink-0 rounded object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex size-10 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
              <ImageIcon className="size-4" />
            </div>
          )}
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium">{primary}</span>
            {handle && <span className="truncate text-xs text-muted-foreground">@{handle}</span>}
          </div>
        </div>
      );
    },
  },
  {
    id: 'reportType',
    header: 'Violation',
    // Prefer the free-text category; else map the UserReportType code (NOSA/SPAM…) to its description.
    cell: ({ row }) => (
      <span className="text-sm">
        {row.original.category || userReportTypeLabel(row.original.userReportType)}
      </span>
    ),
  },
  {
    id: 'reportedBy',
    header: 'Reported By',
    cell: ({ row }) => {
      const r = row.original;
      return (
        <span className="text-sm text-muted-foreground">
          {r.reportedBy || r.reportedByUName || '—'}
        </span>
      );
    },
  },
  {
    id: 'createdAt',
    header: 'Date & Time',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {formatDateTime(row.original.reportedAt)}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const { label, variant } = reportStatusDisplay(row.original.status);
      return <Badge variant={variant}>{label}</Badge>;
    },
  },
];

// --- Proactive Moderation columns (Phase 2) ---

type ProactiveBadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

interface ProactiveStatusDisplay {
  label: string;
  variant: ProactiveBadgeVariant;
}

function proactiveStatusDisplay(status: ProactiveCase['status']): ProactiveStatusDisplay {
  switch (status) {
    case 'approved':
      return { label: 'Approved / bypassed', variant: 'default' };
    case 'rejected':
      return { label: 'Rejected', variant: 'destructive' };
    case 'escalated':
      return { label: 'Human / specialist review', variant: 'destructive' };
    case 'pending':
      return { label: 'Awaiting pipeline', variant: 'outline' };
    case 'heuristic':
      return { label: 'Heuristic scoring', variant: 'outline' };
    case 'standard':
      return { label: 'Standard GPU analysis', variant: 'secondary' };
    case 'deep':
      return { label: 'Frontier model analysis', variant: 'secondary' };
    case 'error':
      return { label: 'Pipeline error', variant: 'destructive' };
    default:
      return { label: status ?? 'Unknown', variant: 'outline' };
  }
}

function riskColor(risk: number | null): string {
  if (risk == null) return 'text-muted-foreground';
  if (risk >= 0.8) return 'text-red-600 font-semibold';
  if (risk >= 0.5) return 'text-amber-600 font-medium';
  return 'text-green-600';
}

export const proactiveColumns: ColumnDef<ProactiveCase, unknown>[] = [
  {
    id: 'post',
    header: 'Post',
    cell: ({ row }) => {
      const r = row.original;
      const thumb = safeMediaSrc(r.thumbnail_url);
      return (
        <div className="flex items-center gap-3">
          {thumb ? (
            <img
              src={thumb}
              alt=""
              className="size-10 shrink-0 rounded object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex size-10 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
              <ImageIcon className="size-4" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <span className="block truncate text-sm">{r.caption_preview || '—'}</span>
            {r.content_description && (
              <span className="block truncate text-xs text-muted-foreground">
                {r.content_description}
              </span>
            )}
          </div>
        </div>
      );
    },
  },
  {
    id: 'creator',
    header: 'Creator',
    cell: ({ row }) => <span className="text-sm">{row.original.creator_name || '—'}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const { label, variant } = proactiveStatusDisplay(row.original.status);
      return <Badge variant={variant}>{label}</Badge>;
    },
  },
  {
    id: 'risk',
    header: 'Risk Score',
    cell: ({ row }) => {
      const risk = row.original.combined_risk;
      return (
        <span className={cn('text-sm', riskColor(risk))}>
          {risk != null ? (risk * 100).toFixed(0) + '%' : '—'}
        </span>
      );
    },
  },
  {
    id: 'ruleMatched',
    header: 'Rule Matched',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">{row.original.rule_matched || '—'}</span>
    ),
  },
  {
    id: 'openedAt',
    header: 'Time',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {formatDateTime(row.original.opened_at)}
      </span>
    ),
  },
];

// --- Moderation Actions Log columns ---

function actionTypeBadge(action: string): { label: string; variant: ProactiveBadgeVariant } {
  switch (action) {
    case 'approved':
      return { label: 'Approved', variant: 'default' };
    case 'rejected':
      return { label: 'Rejected', variant: 'destructive' };
    case 'takedown':
      return { label: 'Taken Down', variant: 'destructive' };
    case 'ban':
      return { label: 'Banned', variant: 'destructive' };
    case 'warn':
      return { label: 'Warning', variant: 'secondary' };
    default:
      return { label: action || 'Unknown', variant: 'outline' };
  }
}

export const actionLogColumns: ColumnDef<ModerationActionEntry, unknown>[] = [
  {
    id: 'time',
    header: 'Time',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {formatDateTime(row.original.timestamp)}
      </span>
    ),
  },
  {
    id: 'actionType',
    header: 'Action',
    cell: ({ row }) => {
      const { label, variant } = actionTypeBadge(row.original.action_type);
      return <Badge variant={variant}>{label}</Badge>;
    },
  },
  {
    id: 'actor',
    header: 'Actor',
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="text-sm">{row.original.actor_name || row.original.actor}</span>
        <span className="text-xs text-muted-foreground">
          {row.original.actor === 'pipeline' ? 'Automated' : 'Human'}
        </span>
      </div>
    ),
  },
  {
    id: 'target',
    header: 'Target',
    cell: ({ row }) => {
      const r = row.original;
      const thumb = safeMediaSrc(r.target_thumbnail);
      return (
        <div className="flex items-center gap-3">
          {thumb ? (
            <img
              src={thumb}
              alt=""
              className="size-10 shrink-0 rounded object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex size-10 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
              <ImageIcon className="size-4" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <span className="block truncate text-sm">{r.target_preview || '—'}</span>
            {r.ai_description && (
              <span className="block truncate text-xs text-muted-foreground">
                {r.ai_description}
              </span>
            )}
          </div>
        </div>
      );
    },
  },
  {
    id: 'reason',
    header: 'Reason / Rule',
    cell: ({ row }) => {
      const r = row.original;
      return (
        <div className="min-w-0">
          {r.rule_name && <span className="block truncate text-sm font-medium">{r.rule_name}</span>}
          {r.reason && (
            <span className="block truncate text-xs text-muted-foreground">{r.reason}</span>
          )}
          {!r.rule_name && !r.reason && <span className="text-sm text-muted-foreground">—</span>}
        </div>
      );
    },
  },
];
